import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Fragment, useState } from 'react';

import BSTableBodyCell from '../table/components/BSTableBodyCell';
import BSTableHeaderCell from '../table/components/BSTableHeaderCell';
import { GameStatus } from '../../../constants/gameConstants';

const Scoreboard = ({ game, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [selectedEnd, setSelectedEnd] = useState(0);
  const [teamAScore, setTeamAScore] = useState(0);
  const [teamBScore, setTeamBScore] = useState(0);

  const sets = Array.isArray(game.sets) ? game.sets : [];
  // Final-score games saved before the current set was stored have no usable
  // currentSet, and an undo can leave it pointing at a removed set. Fall back
  // to the last set so a completed game still shows its scorecard.
  const currentSet = sets.find((set) => set.id === game.currentSet) || sets[sets.length - 1];
  const currentScores = currentSet?.scores || [];

  const teamA = game.teams?.teamA;
  const teamB = game.teams?.teamB;
  const configuredSets = Number(game.gameFormat?.sets) || sets.length;

  if (!teamA || !teamB || sets.length === 0) return null;

  const showSetTotals = configuredSets > 1 || Boolean(game.totalsOnly);
  const hasExtraEndPoints = game.gameFormat?.extraEndGamePoint > 0;
  const setWinPoints = Number(game.gameFormat?.maxTotalPerSet || 0);

  const renderSetTotal = (set, teamId) => {
    const total = set.totalSetScore?.[teamId] ?? 0;
    const wonSetBonus = setWinPoints > 0 && set.winningId === teamId ? ` (${setWinPoints})` : '';
    return `${total}${wonSetBonus}`;
  };

  const createScoreboardArray = (set) => {
    const scoreboard = [];
    const setScores = Array.isArray(set.scores) ? set.scores : [];

    for (let i = 0; i < setScores.length; i++) {
      const teamAScore = setScores[i].teamScores[teamA.id] ?? 0;
      const teamBScore = setScores[i].teamScores[teamB.id] ?? 0;
      const multiplierTeamA = setScores[i].multiplierActive
        ? setScores[i].multiplierActive[teamA.id]
        : false;
      const multiplierTeamB = setScores[i].multiplierActive
        ? setScores[i].multiplierActive[teamB.id]
        : false;

      //teamA total and teamB total is acummulative
      const teamATotal = setScores
        .slice(0, i + 1)
        .reduce((acc, score) => acc + (score.teamScores[teamA.id] ?? 0), 0);
      const teamBTotal = setScores
        .slice(0, i + 1)
        .reduce((acc, score) => acc + (score.teamScores[teamB.id] ?? 0), 0);
      const End = i + 1;
      scoreboard.push({
        teamAScore,
        teamATotal,
        teamBScore,
        teamBTotal,
        End,
        multiplierTeamA,
        multiplierTeamB
      });
    }
    scoreboard.sort((a, b) => b.End - a.End);
    return scoreboard;
  };

  const createPlayoffScoreboard = (playoff) => {
    // playoff ends logic
    // ends: [ { game_end : 1, winningTeam: "" }  ]

    const scoreboard = [];
    const playoffEnds = playoff.ends;

    if (playoffEnds) {
      for (let i = 0; i < playoffEnds.length; i++) {
        //This data does not contain scores it contains ends and the winners of each end, one point gets allogated to each winner.
        const teamAScore = playoffEnds[i].winningTeam === teamA.id ? 1 : 0;
        const teamBScore = playoffEnds[i].winningTeam === teamB.id ? 1 : 0;

        //teamA total and teamB total is acummulative
        const teamATotal = playoffEnds
          .slice(0, i + 1)
          .reduce((acc, score) => acc + (score.winningTeam === teamA.id ? 1 : 0), 0);
        const teamBTotal = playoffEnds
          .slice(0, i + 1)
          .reduce((acc, score) => acc + (score.winningTeam === teamB.id ? 1 : 0), 0);

        const End = i + 1;

        scoreboard.push({
          teamAScore,
          teamATotal,
          teamBScore,
          teamBTotal,
          End
        });
      }

      scoreboard.sort((a, b) => b.End - a.End);
    }

    if (playoff.teamScores) {
      // Edge case where there is a winning team but no ends recorded.
      const teamAScore = playoff.teamScores[teamA.id];
      const teamBScore = playoff.teamScores[teamB.id];
      scoreboard.push({
        teamAScore,
        teamATotal: teamAScore,
        teamBScore,
        teamBTotal: teamBScore,
        End: 1
      });
    }

    return scoreboard;
  };

  const handleEndEdit = (end) => {
    const currentEnd = currentScores.find((score) => score.game_end === end);
    if (!currentEnd) return;
    setSelectedEnd(end);
    setTeamAScore(currentEnd.teamScores?.[teamA.id] ?? 0);
    setTeamBScore(currentEnd.teamScores?.[teamB.id] ?? 0);
    setOpen(true);
  };

  const handleEndSave = () => {
    console.log('Save End');
  };

  const handleClose = () => {
    setOpen(false);
  };

  const row = (
    teamAScore,
    teamATotal,
    teamBSocre,
    teamBTotal,
    End,
    multiplierTeamA,
    multiplierTeamB,
    playoff
  ) => {
    return (
      <TableRow>
        <BSTableBodyCell align="center">
          <Typography variant="caption" color={multiplierTeamA ? 'primary' : 'text'}>
            {teamAScore} {multiplierTeamA && 'PP'}
          </Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography variant="caption">{teamATotal}</Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography
            sx={{ cursor: readOnly ? 'default' : 'pointer' }}
            variant="caption"
            color={playoff ? 'warning.main' : 'primary'}
            fontWeight={'bold'}
            onClick={readOnly ? undefined : () => handleEndEdit(End)}>
            {End}
          </Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography variant="caption" color={multiplierTeamB ? 'primary' : 'text'}>
            {teamBSocre} {multiplierTeamB && 'PP'}
          </Typography>
        </BSTableBodyCell>
        <BSTableBodyCell align="center">
          <Typography variant="caption">{teamBTotal}</Typography>
        </BSTableBodyCell>
      </TableRow>
    );
  };

  const endsScoreboard = () => {
    return (
      <>
        {sets
          .slice()
          .reverse()
          .map((set, index) => (
            <Paper sx={{ p: 2, borderRadius: 5, my: 2 }} key={set.id || index}>
              {sets.length > 1 && !set.totalSetScore && (
                <Typography variant="h5" textAlign={'center'}>
                  {game.gameFormat?.setAsSkin ? `Skin ${set.setNumber}` : `Set ${set.setNumber}`}
                </Typography>
              )}

              {showSetTotals && set.totalSetScore && (
                <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'flex-start'}>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                    minWidth={90}>
                    <Typography variant="body1">Total</Typography>
                    <Typography variant="body1" color={'info.main'} fontSize={'1.5em'}>
                      {renderSetTotal(set, teamA.id)}
                    </Typography>
                  </Box>

                  <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
                    <Typography variant="body1" color={'info.main'} fontWeight={'bold'}>
                      {game.totalsOnly
                        ? 'Totals'
                        : game.gameFormat?.setAsSkin
                          ? `Skin ${set.setNumber}`
                          : `Set ${set.setNumber}`}
                    </Typography>
                    <Typography variant="body1">
                      {set.finalScoreOnly ? 'Final Score' : ''}
                    </Typography>
                  </Box>

                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                    minWidth={90}>
                    <Typography variant="body1">Total</Typography>
                    <Typography variant="body1" color={'info.main'} fontSize={'1.5em'}>
                      {renderSetTotal(set, teamB.id)}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Divider color={'info.main'} sx={{ my: 2 }} />

              {!set.finalScoreOnly ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <BSTableHeaderCell align="center">
                            <Typography variant="body1">Score</Typography>
                          </BSTableHeaderCell>
                          <BSTableHeaderCell align="center">
                            <Typography variant="body1">Total</Typography>
                          </BSTableHeaderCell>
                          <BSTableHeaderCell align="center">
                            <Typography variant="body1" color={'primary'} fontWeight={'bold'}>
                              End
                            </Typography>
                          </BSTableHeaderCell>
                          <BSTableHeaderCell align="center">
                            <Typography variant="body1">Score</Typography>
                          </BSTableHeaderCell>
                          <BSTableHeaderCell align="center">
                            <Typography variant="body1">Total</Typography>
                          </BSTableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {createScoreboardArray(set).map((item) => (
                          <Fragment key={item.End}>
                            {row(
                              item.teamAScore,
                              item.teamATotal,
                              item.teamBScore,
                              item.teamBTotal,
                              item.End,
                              item.multiplierTeamA,
                              item.multiplierTeamB
                            )}
                          </Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
            </Paper>
          ))}
      </>
    );
  };

  const finalTotals = () => {
    let teamATotal = 0;
    let teamBTotal = 0;

    sets.forEach((set) => {
      if (set.totalSetScore) {
        teamATotal += set.totalSetScore[teamA.id] || 0;
        teamBTotal += set.totalSetScore[teamB.id] || 0;
      }
    });

    return (
      <>
        <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'center'}>
          <Typography variant="body1">Total</Typography>
          <Typography variant="body1" color={'success.main'} fontWeight={'bold'}>
            Game Final Total
          </Typography>
          <Typography variant="body1">Total</Typography>
        </Box>

        <Box display={'flex'} justifyContent={'space-evenly'} alignItems={'center'}>
          <Typography variant="body1" color={'success.main'} fontSize={'1.5em'}>
            {teamATotal || 0} ({game.teamTotals?.[teamA.id] ?? 0})
          </Typography>
          <Typography variant="body1">{'Final Score'}</Typography>
          <Typography variant="body1" color={'success.main'} fontSize={'1.5em'}>
            {teamBTotal || 0} ({game.teamTotals?.[teamB.id] ?? 0})
          </Typography>
        </Box>
      </>
    );
  };

  const scoringSummary = () => {
    const teamAExtra = Number(game.teamExtraGamePoints?.[teamA.id] || 0);
    const teamBExtra = Number(game.teamExtraGamePoints?.[teamB.id] || 0);
    const winPoints = Number(game.gameFormat?.winPoints || 0);
    const drawPoints =
      game.gameFormat?.drawPoints !== undefined && game.gameFormat?.drawPoints !== null
        ? Number(game.gameFormat.drawPoints)
        : winPoints / 2;
    const maxTotalPerSet = Number(game.gameFormat?.maxTotalPerSet || 0);
    const teamAGamePoints = Number(game.teamTotals?.[teamA.id] || 0);
    const teamBGamePoints = Number(game.teamTotals?.[teamB.id] || 0);
    const teamAWinPoints =
      game?.playoff?.winningId === teamA.id || game.winningTeamId === teamA.id
        ? winPoints
        : game.winningTeamId === null &&
            (game.status === GameStatus.COMPLETE || game.status === GameStatus.PLAYOFF)
          ? drawPoints
          : 0;
    const teamBWinPoints =
      game?.playoff?.winningId === teamB.id || game.winningTeamId === teamB.id
        ? winPoints
        : game.winningTeamId === null &&
            (game.status === GameStatus.COMPLETE || game.status === GameStatus.PLAYOFF)
          ? drawPoints
          : 0;
    const teamASetPoints = sets.reduce((total, set) => {
      if (maxTotalPerSet === 0) {
        return total;
      }

      if (set.winningId === teamA.id) {
        return total + maxTotalPerSet;
      }

      if (set.isDraw) {
        return total + maxTotalPerSet / 2;
      }

      return total;
    }, 0);
    const teamBSetPoints = sets.reduce((total, set) => {
      if (maxTotalPerSet === 0) {
        return total;
      }

      if (set.winningId === teamB.id) {
        return total + maxTotalPerSet;
      }

      if (set.isDraw) {
        return total + maxTotalPerSet / 2;
      }

      return total;
    }, 0);

    const summaryRow = (leftValue, label, rightValue, addTopSpacing = false) => (
      <Box
        display={'grid'}
        gridTemplateColumns={'1fr 1.6fr 1fr'}
        alignItems={'center'}
        columnGap={2}
        sx={{ mt: addTopSpacing ? 1.5 : 0 }}>
        <Typography variant="h6" color={'success.main'} fontWeight={'bold'} textAlign={'center'}>
          {leftValue}
        </Typography>
        <Typography variant="body1" fontWeight={'bold'} textAlign={'center'}>
          {label}
        </Typography>
        <Typography variant="h6" color={'success.main'} fontWeight={'bold'} textAlign={'center'}>
          {rightValue}
        </Typography>
      </Box>
    );

    if (game.totalsOnly) {
      const totalShotsA = Number(game.sets?.[0]?.totalSetScore?.[teamA.id] || 0);
      const totalShotsB = Number(game.sets?.[0]?.totalSetScore?.[teamB.id] || 0);

      return (
        <Paper sx={{ m: 2, p: 2.5, borderRadius: 5 }}>
          <Typography variant="h5" textAlign={'center'} color={'success.main'} sx={{ mb: 2 }}>
            Scoring Summary
          </Typography>

          {summaryRow(totalShotsA, 'Total Shots', totalShotsB)}
          {summaryRow(teamAGamePoints, 'Total Game Points', teamBGamePoints, true)}
        </Paper>
      );
    }

    return (
      <Paper sx={{ m: 2, p: 2.5, borderRadius: 5 }}>
        <Typography variant="h5" textAlign={'center'} color={'success.main'} sx={{ mb: 2 }}>
          Scoring Summary
        </Typography>

        {hasExtraEndPoints && summaryRow(teamAExtra, 'Extra End Points', teamBExtra)}
        {summaryRow(teamAWinPoints, 'Win Points', teamBWinPoints, hasExtraEndPoints)}
        {showSetTotals && summaryRow(teamASetPoints, 'Total Set Points', teamBSetPoints, true)}
        {summaryRow(teamAGamePoints, 'Total Game Points Overall', teamBGamePoints, true)}
      </Paper>
    );
  };

  // const setTotalScores = (set) => {
  //   const teamATotalSetScore = set.totalSetScore[teamA.id] ?? 0;
  //   const teamBTotalSetScore = set.totalSetScore[teamB.id] ?? 0;
  //   return { teamAScore, teamBScore };
  // };

  return (
    <>
      <Box display={'flex'} mt={3} flexDirection={'column'} alignItems={'center'} width="100%">
        <Typography variant="h4">Scorecard</Typography>
      </Box>

      {finalTotals()}

      {game.playoff && (
        <Paper sx={{ m: 2, p: 2, borderRadius: 5 }}>
          <Typography variant="h5" textAlign={'center'}>
            Tiebreaker
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <BSTableHeaderCell align="center">
                    <Typography variant="body1">Score</Typography>
                  </BSTableHeaderCell>
                  <BSTableHeaderCell align="center">
                    <Typography variant="body1">Total</Typography>
                  </BSTableHeaderCell>
                  <BSTableHeaderCell align="center">
                    <Typography variant="body1" color={'warning.main'} fontWeight={'bold'}>
                      End
                    </Typography>
                  </BSTableHeaderCell>
                  <BSTableHeaderCell align="center">
                    <Typography variant="body1">Score</Typography>
                  </BSTableHeaderCell>
                  <BSTableHeaderCell align="center">
                    <Typography variant="body1">Total</Typography>
                  </BSTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {createPlayoffScoreboard(game.playoff).map((item) => (
                  <Fragment key={item.End}>
                    {row(
                      item.teamAScore,
                      item.teamATotal,
                      item.teamBScore,
                      item.teamBTotal,
                      item.End,
                      false,
                      false,
                      true
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {endsScoreboard()}

      {scoringSummary()}

      {selectedEnd > 0 && (
        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Edit End {selectedEnd}</DialogTitle>
          <DialogContent>
            <DialogContentText>Team A</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="teamA"
              label="Team A Score"
              type="number"
              fullWidth
              value={teamAScore}
              onChange={(e) => setTeamAScore(e.target.value)}
            />
            <DialogContentText>Team B</DialogContentText>
            <TextField
              margin="dense"
              id="teamB"
              label="Team B Score"
              type="number"
              fullWidth
              value={teamBScore}
              onChange={(e) => setTeamBScore(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleEndSave}>Save</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default Scoreboard;
